(() => {
  const config = window.PineCoveLocationsConfig;
  if (!config || typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const CAMP_PARAM_NAME = 'camp';
  const CAMPER_ID_PARAM_NAME = 'camper_id';
  const CLEAR_LOCATION_BYPASS_PARAM_NAME = '_location_cleared';
  const SYNC_PARAM_KEYS = [CAMP_PARAM_NAME, CAMPER_ID_PARAM_NAME];
  const SYNC_FAILURE_LOOP_STORAGE_KEY = 'pine_cove_sync_failure_loop_count';
  const SYNC_FAILURE_LOOP_MAX = 3;
  const LAST_CAMP_HANDLE_STORAGE_KEY = 'pine_cove_last_camp_handle';
  const EMPTY_LOCATION_ATTRIBUTES = {
    location: '',
    _inventory_location: '',
    _camper_id: '',
    _cart_created_at: ''
  };
  const SYNC_MASK_CLASS = 'pine-cove-sync-mask';
  const SYNC_FADE_CLASS = 'pine-cove-sync-fade';
  const FINAL_REVEAL_FALLBACK_MS = 300;
  const CLEAR_LOCATION_LOADING_CLASS = 'is-clearing-location';
  const CLEAR_LOCATION_SPINNER_CLASS = 'pine-cove-clear-location-spinner';

  const initialSearchParams = new URLSearchParams(window.location.search);
  const hasSyncParams = SYNC_PARAM_KEYS.some((key) => initialSearchParams.has(key));

  const debug = Boolean(config.debug);
  const homeUrl = String(config.homeUrl || '/');
  const clearLocationUrl = config.clearLocationUrl;
  const bunkBoxClosedLocationUrl = config.bunkBoxClosedLocationUrl;
  const cartTimeoutMs = Number(config.cartTimeoutMs || 0);
  const hasActiveCampWeeks = Boolean(config.hasActiveCampWeeks);
  const camps = Array.isArray(config.camps) ? config.camps : [];
  const initialCartAttributes = config.initialCartAttributes || {};
  const pageType = String(config.pageType || '');
  const isProductPage = pageType === 'product';
  const isCollectionPage = pageType === 'collection';
  const isCartPage = pageType === 'cart';
  const cartItemCount = Number(config.cartItemCount || 0);
  const collectionInventoryLocation = config.collectionInventoryLocation;
  const collectionTemplateSuffix = String(config.collectionTemplateSuffix || '');
  const webstoreInventoryLocation = String(config.webstoreInventoryLocation || '');
  const productVariantAvailableLocations = Array.isArray(config.productVariantAvailableLocations)
    ? config.productVariantAvailableLocations
    : [];

  const root = document.documentElement;
  const isLocationCollectionTemplate = isCollectionPage && collectionTemplateSuffix === 'location';
  const hasCartLocationAttribute =
    Boolean(String(initialCartAttributes.location || '').trim()) ||
    Boolean(String(initialCartAttributes._inventory_location || '').trim());
  const shouldRedirectLocationTemplateToClearRoute =
    isLocationCollectionTemplate && !hasCartLocationAttribute && !hasSyncParams;

  let hasClearedCartItems = false;
  let isNavigating = false;

  if (debug) {
    console.log('Pine Cove Locations Debug Mode:', debug);
    console.log('Camps:', camps);
    console.log('Initial Cart Attributes:', initialCartAttributes);
    console.log('sessionStorage camp handle:', getLastCampHandle() || '(none)');
  }

  function debugNavigate(reason, url) {
    if (debug) {
      console.log('[Pine Cove Locations] Redirect — reason:', reason, '| url:', url);
      alert('[Pine Cove Locations]\nReason: ' + reason + '\nURL: ' + url);
    }
  }

  function setSyncMask(visible) {
    if (visible) {
      root.classList.remove(SYNC_FADE_CLASS);
      root.classList.add(SYNC_MASK_CLASS);
      return;
    }

    root.classList.add(SYNC_FADE_CLASS);
    window.requestAnimationFrame(() => {
      root.classList.remove(SYNC_MASK_CLASS);
    });
  }

  function getSyncFailureLoopCount() {
    try {
      const value = window.sessionStorage.getItem(SYNC_FAILURE_LOOP_STORAGE_KEY);
      const parsedValue = Number.parseInt(value || '0', 10);
      return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 0;
    } catch (_error) {
      return 0;
    }
  }

  function setSyncFailureLoopCount(nextCount) {
    try {
      window.sessionStorage.setItem(SYNC_FAILURE_LOOP_STORAGE_KEY, String(nextCount));
    } catch (_error) {
      // Ignore storage write errors.
    }
  }

  function resetSyncFailureLoopCount() {
    try {
      window.sessionStorage.removeItem(SYNC_FAILURE_LOOP_STORAGE_KEY);
    } catch (_error) {
      // Ignore storage write errors.
    }
  }

  function incrementSyncFailureLoopCount() {
    const nextCount = getSyncFailureLoopCount() + 1;
    setSyncFailureLoopCount(nextCount);
    return nextCount;
  }

  function getLastCampHandle() {
    try {
      return window.sessionStorage.getItem(LAST_CAMP_HANDLE_STORAGE_KEY) || '';
    } catch (_error) {
      return '';
    }
  }

  function setLastCampHandle(handle) {
    try {
      if (handle) {
        window.sessionStorage.setItem(LAST_CAMP_HANDLE_STORAGE_KEY, handle);
      } else {
        window.sessionStorage.removeItem(LAST_CAMP_HANDLE_STORAGE_KEY);
      }
      if (debug) {
        console.log('[Pine Cove Locations] sessionStorage camp handle set:', handle || '(cleared)');
      }
    } catch (_error) {
      // Ignore storage write errors.
    }
  }

  function clearLastCampHandle() {
    try {
      window.sessionStorage.removeItem(LAST_CAMP_HANDLE_STORAGE_KEY);
      if (debug) {
        console.log('[Pine Cove Locations] sessionStorage camp handle cleared (explicit location change)');
      }
    } catch (_error) {
      // Ignore storage write errors.
    }
  }

  async function updateCart(payload) {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`Failed to update cart: ${response.status}`);
    }
  }

  async function clearCampAttributes() {
    await updateCart({ attributes: EMPTY_LOCATION_ATTRIBUTES });
  }

  async function clearCartItems() {
    const response = await fetch('/cart/clear.js', {
      method: 'POST',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) {
      throw new Error(`Failed to clear cart: ${response.status}`);
    }

    hasClearedCartItems = true;
  }

  function toHandle(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  const campByHandle = new Map();
  camps.forEach((camp) => {
    const handle = toHandle(camp && camp.handle);
    if (handle && !campByHandle.has(handle)) {
      campByHandle.set(handle, camp);
    }
  });

  function findCampByHandle(value) {
    const handle = toHandle(value);
    if (!handle) {
      return null;
    }

    return campByHandle.get(handle) || null;
  }

  function normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  function coerceLocationValues(value) {
    if (Array.isArray(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return [];
      }

      if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            return parsed;
          }
        } catch (_error) {
          // Fall through and parse as comma-separated values.
        }
      }

      return trimmed
        .split(',')
        .map((location) => location.trim())
        .filter(Boolean);
    }

    return [];
  }

  function locationMatches(left, right) {
    const normalizedLeft = normalize(left);
    const normalizedRight = normalize(right);

    if (!normalizedLeft || !normalizedRight) {
      return false;
    }

    return normalizedLeft === normalizedRight || toHandle(normalizedLeft) === toHandle(normalizedRight);
  }

  function hasVariantForLocation(inventoryLocation) {
    if (!inventoryLocation) {
      return true;
    }

    return productVariantAvailableLocations.some((variantLocations) =>
      coerceLocationValues(variantLocations).some((location) => locationMatches(location, inventoryLocation))
    );
  }

  function shouldRedirectForPdpLocation(inventoryLocation) {
    return isProductPage && !hasVariantForLocation(inventoryLocation);
  }

  function resolveCollectionLocationSyncAction() {
    if (!isCollectionPage || hasSyncParams) {
      return 'none';
    }

    const normalizedCollectionInventoryLocation = normalize(collectionInventoryLocation);
    if (!normalizedCollectionInventoryLocation) {
      return hasCartLocationAttribute ? 'clear' : 'none';
    }

    // If the collection uses the store's default webstore inventory location, it isn't
    // camp-specific — don't gate access or redirect.
    if (webstoreInventoryLocation && locationMatches(collectionInventoryLocation, webstoreInventoryLocation)) {
      return 'none';
    }

    const cartInventoryLocation = normalize(initialCartAttributes._inventory_location || '');
    if (!cartInventoryLocation) {
      return 'redirect';
    }

    return locationMatches(collectionInventoryLocation, cartInventoryLocation) ? 'none' : 'redirect';
  }

  function resolveCampLandingPageUrl(camp) {
    if (!camp) {
      return null;
    }

    const landingPage = camp.landingPage;
    if (!landingPage) {
      return null;
    }

    function normalizeLandingPageHref(value) {
      const rawValue = String(value || '').trim();
      if (!rawValue) {
        return '';
      }

      if (/^https?:\/\//i.test(rawValue)) {
        return rawValue;
      }

      if (rawValue.startsWith('/pages/')) {
        return rawValue;
      }

      if (rawValue.startsWith('pages/')) {
        return `/${rawValue}`;
      }

      if (rawValue.startsWith('/')) {
        return rawValue;
      }

      return `/pages/${rawValue}`;
    }

    let landingPageHref = '';
    if (typeof landingPage === 'string') {
      landingPageHref = normalizeLandingPageHref(landingPage);
    } else if (typeof landingPage === 'object') {
      landingPageHref =
        landingPage.url ||
        landingPage.path ||
        normalizeLandingPageHref(landingPage.value) ||
        normalizeLandingPageHref(landingPage.handle);
    }

    if (!landingPageHref) {
      return null;
    }

    try {
      return new URL(landingPageHref, window.location.origin);
    } catch (_error) {
      return null;
    }
  }

  function normalizePathname(pathname) {
    const value = String(pathname || '').trim();
    if (!value || value === '/') {
      return '/';
    }

    return value.replace(/\/+$/, '') || '/';
  }

  const normalizedCurrentPath = normalizePathname(window.location.pathname);

  const normalizedHomePath = (() => {
    try {
      return normalizePathname(new URL(homeUrl, window.location.origin).pathname);
    } catch (_error) {
      return '/';
    }
  })();

  const campByPath = new Map();
  camps.forEach((camp) => {
    const landingUrl = resolveCampLandingPageUrl(camp);
    if (!landingUrl) {
      return;
    }

    const landingPath = normalizePathname(landingUrl.pathname);
    if (!campByPath.has(landingPath)) {
      campByPath.set(landingPath, camp);
    }
  });

  function findCampByCurrentPath() {
    return campByPath.get(normalizedCurrentPath) || null;
  }

  function isCampScopedPage() {
    if (findCampByCurrentPath()) {
      return true;
    }

    if (hasSyncParams) {
      return true;
    }

    if (isLocationCollectionTemplate) {
      return true;
    }

    if (isCollectionPage && normalize(collectionInventoryLocation)) {
      const isWebstoreCollection =
        Boolean(webstoreInventoryLocation) && locationMatches(collectionInventoryLocation, webstoreInventoryLocation);
      if (!isWebstoreCollection) {
        return true;
      }
    }

    return false;
  }

  function resolveLandingPageSyncAction() {
    const currentPathCamp = findCampByCurrentPath();
    if (!currentPathCamp) {
      return 'none';
    }

    const campLocation = normalize(currentPathCamp.name || '');
    const campInventoryLocation = normalize(currentPathCamp.inventoryLocation || '');
    const cartLocation = normalize(initialCartAttributes.location || '');
    const cartInventoryLocation = normalize(initialCartAttributes._inventory_location || '');

    const hasMatchingLocation = locationMatches(campLocation, cartLocation);
    const hasMatchingInventoryLocation = campInventoryLocation
      ? locationMatches(campInventoryLocation, cartInventoryLocation)
      : !cartInventoryLocation;

    if (debug) {
      console.log('hasMatchingLocation', hasMatchingLocation)
    }
    
    return hasMatchingLocation && hasMatchingInventoryLocation ? 'none' : 'sync';
  }

  function resolveClearLocationRouteUrl() {
    const rawValue = String(clearLocationUrl || '').trim();
    if (!rawValue) {
      return null;
    }

    let normalizedHref = rawValue;
    if (!/^https?:\/\//i.test(rawValue)) {
      if (rawValue.startsWith('/')) {
        normalizedHref = rawValue;
      } else if (rawValue.startsWith('pages/')) {
        normalizedHref = `/${rawValue}`;
      } else {
        normalizedHref = `/pages/${rawValue}`;
      }
    }

    try {
      return new URL(normalizedHref, window.location.origin);
    } catch (_error) {
      return null;
    }
  }

  function resolveBunkBoxClosedLocationUrl() {
    const rawValue = String(bunkBoxClosedLocationUrl || '').trim();
    if (!rawValue) {
      return null;
    }

    let normalizedHref = rawValue;
    if (!/^https?:\/\//i.test(rawValue)) {
      if (rawValue.startsWith('/')) {
        normalizedHref = rawValue;
      } else if (rawValue.startsWith('pages/')) {
        normalizedHref = `/${rawValue}`;
      } else {
        normalizedHref = `/pages/${rawValue}`;
      }
    }

    try {
      return new URL(normalizedHref, window.location.origin);
    } catch (_error) {
      return null;
    }
  }

  function redirectToBunkBoxClosedLocationUrl() {
    const nextUrl = resolveBunkBoxClosedLocationUrl();
    if (!nextUrl) {
      console.error('[Pine Cove Locations] Bunk Box closed location URL setting is missing or invalid');
      return;
    }

    debugNavigate('No active camp weeks — redirecting to bunk box closed page', nextUrl.toString());
    isNavigating = true;
    window.location.assign(nextUrl.toString());
  }

  function redirectToClearLocationRoute(forceReloadIfSame = false, markLocationCleared = false) {
    const nextUrl = resolveClearLocationRouteUrl();
    if (!nextUrl) {
      console.error('[Pine Cove Locations] Clear location route setting is missing or invalid');
      return;
    }

    SYNC_PARAM_KEYS.forEach((key) => nextUrl.searchParams.delete(key));
    const shouldMarkLocationCleared = markLocationCleared || hasClearedCartItems;
    if (shouldMarkLocationCleared) {
      nextUrl.searchParams.set(CLEAR_LOCATION_BYPASS_PARAM_NAME, '1');
    } else {
      nextUrl.searchParams.delete(CLEAR_LOCATION_BYPASS_PARAM_NAME);
    }

    if (window.location.pathname === nextUrl.pathname && window.location.search === nextUrl.search) {
      if (forceReloadIfSame) {
        debugNavigate('Already on clear location route — forcing reload', window.location.href);
        isNavigating = true;
        window.location.reload();
      }
      return;
    }

    debugNavigate('Location mismatch or missing — redirecting to location selection', nextUrl.toString());
    isNavigating = true;
    window.location.assign(nextUrl.toString());
  }

  function redirectToCampLandingPagePreservingParams(camp, forceReloadIfSame = false) {
    if (!camp) {
      return false;
    }

    const params = new URLSearchParams(window.location.search);
    const nextUrl = resolveCampLandingPageUrl(camp);
    if (!nextUrl) {
      return false;
    }

    SYNC_PARAM_KEYS.forEach((key) => {
      const value = params.get(key);
      if (value) {
        nextUrl.searchParams.set(key, value);
      }
    });

    if (
      window.location.pathname === nextUrl.pathname &&
      window.location.search === nextUrl.search &&
      window.location.hash === nextUrl.hash
    ) {
      if (forceReloadIfSame) {
        debugNavigate('Already on camp landing page — forcing reload to sync attributes', window.location.href);
        isNavigating = true;
        window.location.reload();
      }
      return true;
    }

    debugNavigate('Camp resolved — redirecting to camp landing page', nextUrl.toString());
    isNavigating = true;
    window.location.replace(nextUrl.toString());
    return true;
  }

  function normalizeAttributeValue(value) {
    return String(value == null ? '' : value);
  }

  function diffAttributes(currentAttributes, desiredAttributes) {
    const changed = {};

    Object.keys(desiredAttributes).forEach((key) => {
      const currentValue = normalizeAttributeValue(currentAttributes && currentAttributes[key]);
      const desiredValue = normalizeAttributeValue(desiredAttributes[key]);

      if (currentValue !== desiredValue) {
        changed[key] = desiredAttributes[key];
      }
    });

    return changed;
  }

  function shouldIgnoreLocationLinkClick(event, link) {
    if (event.defaultPrevented) {
      return true;
    }

    if (event.button !== 0) {
      return true;
    }

    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return true;
    }

    if (link.target && link.target !== '_self') {
      return true;
    }

    const href = link.getAttribute('href') || '';
    if (!href || href.startsWith('javascript:') || href.startsWith('mailto:') || href.startsWith('tel:')) {
      return true;
    }

    return false;
  }

  function isHomeLink(link) {
    const href = link.getAttribute('href') || '';
    if (!href) {
      return false;
    }

    try {
      const url = new URL(href, window.location.origin);
      return (
        url.origin === window.location.origin &&
        normalizePathname(url.pathname) === normalizedHomePath &&
        !url.search &&
        !url.hash
      );
    } catch (_error) {
      return false;
    }
  }

  function resolveCurrentCamp() {
    const params = new URLSearchParams(window.location.search);
    const campParam = params.get(CAMP_PARAM_NAME);
    if (campParam) {
      const campFromParam = findCampByHandle(campParam);
      if (campFromParam) {
        return campFromParam;
      }
    }

    return findCampByHandle(initialCartAttributes.location || '');
  }

  function isCurrentHomePage() {
    return normalizedCurrentPath === normalizedHomePath;
  }

  function redirectHomePageToCurrentCampLandingPage() {
    if (!isCurrentHomePage()) {
      return false;
    }

    const params = new URLSearchParams(window.location.search);
    if (params.get(CLEAR_LOCATION_BYPASS_PARAM_NAME) === '1') {
      return false;
    }

    return redirectToCampLandingPagePreservingParams(resolveCurrentCamp());
  }

  function setClearLocationButtonLoadingState(link) {
    link.classList.add(CLEAR_LOCATION_LOADING_CLASS);
    link.setAttribute('aria-busy', 'true');

    if (!link.style.width || !link.style.height) {
      const linkRect = link.getBoundingClientRect();
      if (!link.style.width) {
        link.style.width = `${Math.ceil(linkRect.width)}px`;
      }
      if (!link.style.height) {
        link.style.height = `${Math.ceil(linkRect.height)}px`;
      }
    }

    link.textContent = '';
    const spinner = document.createElement('span');
    spinner.className = CLEAR_LOCATION_SPINNER_CLASS;
    spinner.setAttribute('aria-hidden', 'true');
    link.appendChild(spinner);
  }

  async function clearLocationState() {
    try {
      await clearCartItems();
    } catch (error) {
      console.error('[Pine Cove Locations] Failed to clear cart items', error);
    }

    try {
      await clearCampAttributes();
    } catch (error) {
      console.error('[Pine Cove Locations] Failed to clear camp attributes', error);
    }
  }

  function installHomeToLocationLinkHandler() {
    document.addEventListener('click', (event) => {
      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;

      if (!link || shouldIgnoreLocationLinkClick(event, link)) {
        return;
      }

      if (link.hasAttribute('data-clear-location')) {
        return;
      }

      if (!isHomeLink(link)) {
        return;
      }

      const currentCamp = resolveCurrentCamp();
      if (!currentCamp) {
        return;
      }

      event.preventDefault();
      redirectToCampLandingPagePreservingParams(currentCamp);
    });
  }

  function installClearLocationHandler() {
    document.addEventListener('click', (event) => {
      const link = event.target instanceof Element ? event.target.closest('a[data-clear-location]') : null;

      if (!link || shouldIgnoreLocationLinkClick(event, link)) {
        return;
      }

      if (link.classList.contains(CLEAR_LOCATION_LOADING_CLASS)) {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      setClearLocationButtonLoadingState(link);
      clearLastCampHandle();
      clearLocationState().finally(() => {
        redirectToClearLocationRoute(true, true);
      });
    });
  }

  function installBackForwardCacheHandler() {
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        debugNavigate('Page restored from back/forward cache — reloading to re-sync state', window.location.href);
        setSyncMask(true);
        window.location.reload();
      }
    });
  }

  async function syncCampAndCamperAttributes() {
    if (debug) {
      console.log('[Pine Cove Locations] syncCampAndCamperAttributes — state:', {
        pageType,
        collectionInventoryLocation,
        collectionTemplateSuffix,
        isLocationCollectionTemplate,
        shouldRedirectLocationTemplateToClearRoute,
        hasCartLocationAttribute,
        hasSyncParams,
        hasActiveCampWeeks
      });
    }

    if (cartTimeoutMs > 0 && hasCartLocationAttribute && cartItemCount > 0) {
      const createdAt = initialCartAttributes._cart_created_at;
      if (createdAt) {
        const createdAtMs = new Date(createdAt).getTime();
        if (Number.isFinite(createdAtMs)) {
          const elapsedMs = Date.now() - createdAtMs;
          if (debug) {
            console.log('[Pine Cove Locations] Cart age:', Math.round(elapsedMs / 60000), 'min | timeout:', Math.round(cartTimeoutMs / 60000), 'min');
          }
          if (elapsedMs >= cartTimeoutMs) {
            if (debug) {
              console.log('[Pine Cove Locations] Cart timeout expired — clearing cart and redirecting home');
            }
            clearLastCampHandle();
            await clearLocationState();
            debugNavigate('Cart timeout expired', homeUrl);
            isNavigating = true;
            window.location.assign(homeUrl);
            return;
          }
        }
      }
    }

    if (!hasActiveCampWeeks) {
      const isCampSurface = isCampScopedPage();
      if (debug) {
        console.log('[Pine Cove Locations] No active camp weeks found.', {
          isCampSurface,
          isOnClosedLocationPage,
          hasCartLocationAttribute
        });
      }

      if (isCampSurface && !isOnClosedLocationPage) {
        await clearLocationState();
        redirectToBunkBoxClosedLocationUrl();
        return;
      }

      if (hasCartLocationAttribute) {
        await clearLocationState();
        debugNavigate('No active camp weeks — cleared stale camp cart, reloading', window.location.href);
        isNavigating = true;
        window.location.reload();
      }
      return;
    }

    if (isCartPage && cartItemCount === 0 && !hasCartLocationAttribute && !hasSyncParams) {
      const lastHandle = getLastCampHandle();
      if (debug) {
        console.log('[Pine Cove Locations] Empty cart recovery check — cartItemCount:', cartItemCount, 'hasCartLocationAttribute:', hasCartLocationAttribute, 'sessionStorage handle:', lastHandle || '(none)');
      }
      if (lastHandle) {
        const lastCamp = findCampByHandle(lastHandle);
        if (lastCamp) {
          const landingUrl = resolveCampLandingPageUrl(lastCamp);
          if (debug) {
            console.log('[Pine Cove Locations] Recovering — redirecting to:', landingUrl ? landingUrl.toString() : '(no landing URL)');
          }
          if (landingUrl) {
            landingUrl.searchParams.set(CAMP_PARAM_NAME, lastHandle);
            debugNavigate('Empty cart with no location attributes — recovering camp from sessionStorage (' + lastHandle + ')', landingUrl.toString());
            isNavigating = true;
            window.location.replace(landingUrl.toString());
            return;
          }
        } else if (debug) {
          console.log('[Pine Cove Locations] sessionStorage handle did not match any camp:', lastHandle);
        }
      }
    }

    if (shouldRedirectLocationTemplateToClearRoute) {
      if (debug) console.log('[Pine Cove Locations] redirect source: shouldRedirectLocationTemplateToClearRoute');
      redirectToClearLocationRoute();
      return;
    }

    const collectionLocationSyncAction = resolveCollectionLocationSyncAction();
    if (debug) console.log('[Pine Cove Locations] collectionLocationSyncAction:', collectionLocationSyncAction);
    if (collectionLocationSyncAction === 'clear') {
      await clearLocationState();
      debugNavigate('Collection has no inventory location but cart does — clearing location and reloading', window.location.href);
      isNavigating = true;
      window.location.reload();
      return;
    }
    if (collectionLocationSyncAction === 'redirect') {
      if (debug) console.log('[Pine Cove Locations] redirect source: collectionLocationSyncAction === redirect');
      redirectToClearLocationRoute();
      return;
    }

    const currentPathCamp = findCampByCurrentPath();

    if (redirectHomePageToCurrentCampLandingPage()) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const campParam = params.get(CAMP_PARAM_NAME);
    const camperId = params.get(CAMPER_ID_PARAM_NAME);
    const desiredAttributes = {};

    let matchedCamp = currentPathCamp;

    if (debug) {
      console.log('campParam', campParam)
      console.log('camperId', camperId)
      console.log('matchedCamp', matchedCamp)
    }
    if (matchedCamp) {
      desiredAttributes.location = matchedCamp.name;
      desiredAttributes._inventory_location = matchedCamp.inventoryLocation || '';
    } else if (campParam) {
      matchedCamp = findCampByHandle(campParam);

      if (matchedCamp) {
        if (debug) {
          console.log('Matched Camp:', matchedCamp);
        }
        desiredAttributes.location = matchedCamp.name;
        desiredAttributes._inventory_location = matchedCamp.inventoryLocation || '';
      }
    }

    if (camperId) {
      desiredAttributes._camper_id = camperId;
    }

    const currentInventoryLocation =
      desiredAttributes._inventory_location || initialCartAttributes._inventory_location || '';

    if (shouldRedirectForPdpLocation(currentInventoryLocation)) {
      if (debug) {
        alert('No PDP variant found for inventory location: ' + currentInventoryLocation);
      }
      const currentCamp =
        matchedCamp || findCampByHandle(desiredAttributes.location || initialCartAttributes.location || '');
      if (redirectToCampLandingPagePreservingParams(currentCamp)) {
        return;
      }

      if (debug) console.log('[Pine Cove Locations] redirect source: shouldRedirectForPdpLocation — no camp match');
      redirectToClearLocationRoute();
      return;
    }

    if (Object.keys(desiredAttributes).length === 0) {
      return;
    }

    const changedAttributes = diffAttributes(initialCartAttributes, desiredAttributes);
    const hasCampAttributeChanges =
      Boolean(matchedCamp) &&
      (Object.prototype.hasOwnProperty.call(changedAttributes, 'location') ||
        Object.prototype.hasOwnProperty.call(changedAttributes, '_inventory_location'));

    if (debug) {
      console.log('changedAttributes', changedAttributes)
    }
    
    if (Object.keys(changedAttributes).length === 0) {
      if (debug) {
        console.log('No cart attribute changes detected; skipping update.');
      }
      if (matchedCamp) {
        setLastCampHandle(matchedCamp.handle);
        if (redirectToCampLandingPagePreservingParams(matchedCamp)) {
          return;
        }
      }
      return;
    }

    const shouldClearCart = hasCampAttributeChanges;

    if (shouldClearCart) {
      desiredAttributes._cart_created_at = new Date().toISOString();
    }

    if (debug) {
      console.log('Should Clear Cart:', shouldClearCart);
    }
    const attributesToUpdate = shouldClearCart ? desiredAttributes : changedAttributes;
    if (shouldClearCart) {
      if (debug) {
        console.log('Clearing cart before location update');
      }
      await clearCartItems();

      if (debug) {
        console.log('Updating cart attributes payload:', { attributes: attributesToUpdate });
      }
      await updateCart({ attributes: attributesToUpdate });
    } else {
      const updatePayload = { attributes: attributesToUpdate };

      if (debug) {
        console.log('Updating Cart Payload:', updatePayload);
      }
      await updateCart(updatePayload);
    }

    const updatedInventoryLocation = attributesToUpdate._inventory_location || currentInventoryLocation || '';
    const updatedCamp =
      matchedCamp ||
      findCampByHandle(attributesToUpdate.location || desiredAttributes.location || initialCartAttributes.location || '');

    if (updatedCamp) {
      setLastCampHandle(updatedCamp.handle);
    }

    if (shouldRedirectForPdpLocation(updatedInventoryLocation) && debug) {
      alert('No PDP variant found after location update: ' + updatedInventoryLocation);
    }

    if (updatedCamp) {
      redirectToCampLandingPagePreservingParams(updatedCamp, true);
      return;
    }

    debugNavigate('Cart attributes updated but no camp matched — reloading to apply changes', window.location.href);
    isNavigating = true;
    window.location.reload();
  }

  const closedLocationUrl = resolveBunkBoxClosedLocationUrl();
  const isOnClosedLocationPage = closedLocationUrl && normalizePathname(closedLocationUrl.pathname) === normalizedCurrentPath;

  setSyncMask(true);
  if (
    ((!hasActiveCampWeeks && isOnClosedLocationPage) ||
    (!hasActiveCampWeeks && !isCampScopedPage() && !hasCartLocationAttribute) ||
    (hasActiveCampWeeks &&
    !hasSyncParams &&
    !shouldRedirectLocationTemplateToClearRoute &&
    resolveCollectionLocationSyncAction() === 'none' &&
    resolveLandingPageSyncAction() === 'none'))
  ) {
    setSyncMask(false);
  }

  syncCampAndCamperAttributes()
    .then(() => {
      resetSyncFailureLoopCount();
    })
    .catch((error) => {
      console.error('[Pine Cove Locations] Failed to sync cart attributes', error);

      if (!isNavigating) {
        const failureLoopCount = incrementSyncFailureLoopCount();
        if (failureLoopCount >= SYNC_FAILURE_LOOP_MAX) {
          resetSyncFailureLoopCount();
          if (!redirectToCampLandingPagePreservingParams(resolveCurrentCamp())) {
            if (debug) console.log('[Pine Cove Locations] redirect source: catch — max failure loop, no camp match');
            redirectToClearLocationRoute();
          }
          return;
        }

        if (debug) console.log('[Pine Cove Locations] redirect source: catch — sync error, loop count:', failureLoopCount);
        redirectToClearLocationRoute();
      }
    })
    .finally(() => {
      if (!isNavigating) {
        setSyncMask(false);
        window.setTimeout(() => {
          if (!isNavigating) {
            setSyncMask(false);
          }
        }, FINAL_REVEAL_FALLBACK_MS);
      }
    });

  installHomeToLocationLinkHandler();
  installClearLocationHandler();
  installBackForwardCacheHandler();
})();
